import React, {
    ChangeEvent,
    DetailedHTMLProps,
    FC,
    forwardRef,
    TextareaHTMLAttributes,
    useRef,
    useState,
} from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { LockFill } from '../../../assets/icons'
import { colors } from '../../../theme/palette'

type RuleNames = 'label' | 'textarea' | 'container' | 'span' | 'lock' | 'count'

interface StyleProps {
    query: string
    hasError: boolean
}

export type TextAreaProps = {
    label: string
    required?: boolean
    maxCount?: number
    rows?: number
    cols?: number
    hasError?: boolean
} & Omit<
    DetailedHTMLProps<
        TextareaHTMLAttributes<HTMLTextAreaElement>,
        HTMLTextAreaElement
    >,
    'size'
>

const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    container: {
        position: 'relative',
        display: 'block',
    },
    label: {
        position: 'static',
        width: '100%',
        '&:focus-within > span': {
            color: colors.green500,
            fontSize: '0.75rem',
            top: '2px',
        },
        color: ({ hasError }) => (hasError ? colors.error : colors.labelColor),
    },
    textarea: {
        height: '100%',
        minHeight: '3rem',
        // border: `1px solid ${colors.borderColor}`,
        border: ({ hasError }) =>
            `1px solid ${hasError ? colors.error : colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1rem',
        paddingLeft: '0.75rem',
        paddingTop: '30px',
        resize: 'none',
        '&::placeholder': {
            opacity: '0',
        },
        color: colors.black,
        '&:not(:placeholder-shown) + span': {
            color: colors.green500,
            fontSize: '0.75rem',
        },
        '&:not(:placeholder-shown)': {
            paddingTop: '30px',
        },
        '&:focus': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
            paddingTop: '30px',
        },
        '&:active': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
            paddingTop: '30px',
        },
        '&:disabled': {
            backgroundColor: '#F5F6F8',
            color: colors.gray800,
        },
    },
    span: {
        position: 'absolute',
        top: '2px',
        left: '2px',
        right: '20px',
        fontSize: '1rem',
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '10px 0.75rem 5px',
        transitionDuration: '300ms',
        color: ({ hasError }) => (hasError ? colors.error : colors.labelColor),
    },
    lock: {
        position: 'absolute',
        top: '0',
        right: '10px',
        opacity: '.5',
    },
    count: {
        position: 'relative',
        color: colors.black,
        fontSize: '15px',
        textAlign: 'right',
        paddingTop: '5px',
    },
})

const TextArea: FC<TextAreaProps> = forwardRef<
    HTMLTextAreaElement,
    TextAreaProps
>(
    (
        {
            label,
            required,
            maxCount,
            onChange,
            rows = 5,
            cols = 5,
            hasError,
            ...props
        },
        ref
    ) => {
        const [query] = useState<string>('')
        const theme = useTheme<Jss.Theme>()
        const classes = useStyles({ ...{ query, hasError }, theme })
        const [count, setCount] = useState(
            props.defaultValue ? String(props.defaultValue).length : 0
        )
        const containerRef = useRef(null)

        const autoResize = (e: ChangeEvent<HTMLTextAreaElement>): void => {
            e.currentTarget.style.height = 'auto'
            e.currentTarget.style.height = `${
                e.currentTarget.scrollHeight + 10
            }px`

            if (containerRef.current) {
                containerRef.current.style.height = `${
                    e.currentTarget.scrollHeight + 10
                }px`
            }
        }

        return (
            <>
                <div className={classes.container} ref={containerRef}>
                    <label htmlFor={label} className={classes.label}>
                        <textarea
                            {...props}
                            ref={ref}
                            required={required}
                            className={classes.textarea}
                            id={label}
                            rows={rows}
                            cols={cols}
                            placeholder={label}
                            onFocus={(e: ChangeEvent<HTMLTextAreaElement>) => {
                                setCount(e.target.value.length)
                                onChange(e)
                                autoResize(e)
                            }}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                                setCount(e.target.value.length)
                                onChange(e)
                                autoResize(e)
                            }}
                            maxLength={maxCount}
                        />
                        <span className={classes.span}>
                            {label} {required && <span> *</span>}
                        </span>
                        {props.disabled ? (
                            <span className={classes.lock}>
                                <LockFill width="16px" fill={colors.gray900} />
                            </span>
                        ) : null}
                    </label>
                </div>
                <div className={classes.count}>
                    {count} / {maxCount}
                </div>
            </>
        )
    }
)

TextArea.defaultProps = {
    maxCount: 300,
}

export { TextArea }
